import { useState } from "react";
import { Bold, Heading2, List, Eye, X, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { renderBlock } from "@/lib/richtext";

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

const CATEGORIES = ["Education", "Market Note", "Research", "Regulation", "Process"];

export const ArticleEditor = ({ article, onSaved, onCancel }) => {
  const [title, setTitle] = useState(article?.title || "");
  const [category, setCategory] = useState(article?.category || CATEGORIES[0]);
  const [excerpt, setExcerpt] = useState(article?.excerpt || "");
  const [body, setBody] = useState(Array.isArray(article?.body) ? article.body.join("\n\n") : article?.body || "");
  const [cover, setCover] = useState(article?.cover_image || "");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-image", fd);
      setCover(res.data.path);
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const insert = (before, after = "") => {
    const el = document.getElementById("article-body-textarea");
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = body.slice(s, e) || "text";
    setBody(body.slice(0, s) + before + sel + after + body.slice(e));
    setTimeout(() => { el.focus(); el.setSelectionRange(s + before.length, s + before.length + sel.length); }, 0);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title, category, excerpt, body, cover_image: cover || null };
      const res = article ? await api.put(`/admin/articles/${article.slug}`, payload) : await api.post("/admin/articles", payload);
      toast.success(article ? "Article updated." : "Article published.");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const blocks = body.split(/\n\s*\n/).filter((b) => b.trim());

  return (
    <form data-testid="article-editor-form" onSubmit={save} className="border border-[#6D28D9]/25 bg-[#F6F4FD]/70 p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#6D28D9]/90">{article ? "Edit note" : "New insight note"}</p>
        <button type="button" data-testid="article-editor-cancel" onClick={onCancel} className="text-slate-500 transition-colors hover:text-rose-700"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_200px]">
        <input data-testid="article-title-input" required minLength={4} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className={inputCls} />
        <select data-testid="article-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <textarea data-testid="article-excerpt-input" required minLength={10} rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="One-line summary shown on the card" className={`${inputCls} mt-4 resize-none`} />

      <div className="mt-4">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Cover image</p>
        {cover ? (
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
            <img data-testid="article-cover-preview" src={fileUrl(cover)} alt="Cover" className="h-28 w-full max-w-xs border border-slate-200 object-cover sm:w-48" />
            <div className="flex items-center gap-2">
              <label className="flex cursor-pointer items-center gap-1.5 border border-slate-200 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-700 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9]">
                <ImagePlus className="h-3 w-3" /> Replace
                <input data-testid="article-cover-input" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadCover} className="hidden" />
              </label>
              <button type="button" data-testid="article-cover-remove" onClick={() => setCover("")} className="flex items-center gap-1.5 border border-slate-200 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:border-rose-600/40 hover:text-rose-700">
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          </div>
        ) : (
          <label data-testid="article-cover-dropzone" className="mt-2 flex cursor-pointer items-center justify-center gap-2 border border-dashed border-slate-300 bg-[#FFFFFF]/40 px-4 py-6 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9]">
            {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</> : <><ImagePlus className="h-3.5 w-3.5" /> Upload a cover image (optional)</>}
            <input data-testid="article-cover-input" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadCover} disabled={uploading} className="hidden" />
          </label>
        )}
      </div>

      <div className="mt-4 flex items-center gap-1 border border-slate-200 border-b-0 bg-[#FFFFFF]/60 px-2 py-1.5">
        {[["Heading", Heading2, () => insert("## ")], ["Bold", Bold, () => insert("**", "**")], ["List", List, () => insert("- ")]].map(([label, Icon, fn]) => (
          <button key={label} type="button" data-testid={`article-toolbar-${label.toLowerCase()}`} onClick={fn} title={label} className="flex items-center gap-1.5 px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-[#6D28D9]">
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
        <span className="ml-auto hidden font-mono2 text-[10px] text-slate-400 sm:block">Blank line = new paragraph</span>
        <button type="button" data-testid="article-preview-toggle" onClick={() => setPreview(!preview)} className={`ml-2 flex items-center gap-1.5 px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors ${preview ? "text-[#6D28D9]" : "text-slate-500 hover:text-[#6D28D9]"}`}>
          <Eye className="h-3.5 w-3.5" /> Preview
        </button>
      </div>
      {preview ? (
        <div data-testid="article-preview" className="min-h-[320px] space-y-6 border border-slate-200 bg-[#FFFFFF]/40 p-6">
          {blocks.length ? blocks.map((b, i) => renderBlock(b, i)) : <p className="text-sm text-slate-500">Nothing to preview yet.</p>}
        </div>
      ) : (
        <textarea
          id="article-body-textarea"
          data-testid="article-body-input"
          required
          minLength={20}
          rows={14}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={"Write the note here.\n\n## Use a heading\n\nParagraphs are separated by a blank line. **Bold** text with double asterisks.\n\n- Bullet one\n- Bullet two"}
          className={`${inputCls} resize-y font-mono2 text-[13px] leading-relaxed`}
        />
      )}
      <div className="mt-5 flex items-center justify-between">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{body.trim().split(/\s+/).filter(Boolean).length} words</p>
        <button data-testid="article-save-button" type="submit" disabled={saving} className="bg-[#6D28D9] px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
          {saving ? "Saving…" : article ? "Save changes" : "Publish"}
        </button>
      </div>
    </form>
  );
};
